/**
 * 🔐 **Whether to send somebody away to sign in** — T88, D39.
 *
 * ## 🔴 The fault this exists to end
 *
 * 🎯 **haugur, 2026-09-10:** *"if player has a browser open for long he
 * automatically signs out but can keep playing ad not signed in."*
 *
 * **The game credential lives in `sessionStorage`, which belongs to one tab and
 * dies with it.** Come back from a bookmark and you are nobody: you can walk,
 * look and press F8, you cannot build, **and nothing tells you.** On 2026-09-10
 * three bug reports were filed from exactly that state, and the only reason
 * anybody noticed is that the byline shipped the same morning said *an
 * anonymous player on staging* while a real person was playing.
 *
 * ## 🔴 Why the decision is here and not beside `window.location`
 *
 * ⚠️ **A redirect is the one client behaviour that cannot be tried twice.** Get
 * it wrong and the browser bounces between two sites forever, and **a loop is
 * worse than being stranded** — a stranded player can still report a bug, and a
 * looping one cannot even read the screen. So the decision is arithmetic over
 * plain values, where a test can see every branch, and `main.ts` is left with
 * one line that navigates.
 */

/** What the client should do about an unidentified connection. */
export type SignInAction =
  /** Do nothing. Either we are somebody, or being nobody is correct here. */
  | 'stay'
  /** Send the browser to the sign-in address. */
  | 'go'
  /**
   * We already went and came back as nobody. **Say so on screen and do not
   * move.** 🔴 The one outcome that must never become another `go`.
   */
  | 'strand';

export interface SignInSituation {
  /** Who the server says we are. `null` is an unidentified connection. */
  readonly player: { readonly displayName: string } | null;
  /** The finished sign-in URL the server sent, if it sent one. */
  readonly signIn: string | undefined;
  /** The host the page itself was served from. */
  readonly pageHost: string;
  /** The host of the game server this client is talking to. */
  readonly serverHost: string;
  /** Whether this tab has already been sent away once and come back. */
  readonly alreadyTried: boolean;
}

/**
 * 🔐 **Decide, once, what to do with a welcome.**
 *
 * The order of these tests is the whole design, so each one says why it comes
 * where it does.
 */
export function signInAction(s: SignInSituation): SignInAction {
  /* ✅ We are somebody. Nothing to do — and this is the case that CLEARS the
     marker, so a later sign-out gets a fresh attempt rather than being told it
     has already tried. */
  if (s.player !== null) return 'stay';

  /* 🔴 **Absent means being nobody is correct here.** Development runs
     `ALLOW_ANONYMOUS_BUILD=true` with no accounts at all, so there is nobody to
     become and a redirect would send a developer to a login form that cannot
     help them. **One field carries both facts and they cannot disagree.** */
  if (s.signIn === undefined || s.signIn === '') return 'stay';

  /**
   * 🔴 **A client pointed at ANOTHER server does not send itself away.**
   *
   * ⚠️ **This is the `?server=` instrument** — `pnpm --filter @lofi/client dev`
   * against a deployed server is *the only way to see a client change before
   * merging it*, and the handoff says so. Without this test, opening it against
   * staging would throw the developer to the staging website and bring them
   * back to `play.staging` — **abandoning the very build they were testing.**
   */
  if (s.pageHost !== s.serverHost) return 'stay';

  /* 🔴 **The loop guard.** We went, we came back, we are still nobody — so the
     hand-over did not work and going again would only do it faster. */
  if (s.alreadyTried) return 'strand';

  /* ⚠️ **Our own server sent this, and it is still checked.** A destination
     that is not plain http(s) is a bug rather than an attack, but `javascript:`
     and friends are exactly the shape that turns a redirect into something
     else, and refusing costs one line. */
  if (!isPlainWebUrl(s.signIn)) return 'strand';

  return 'go';
}

/** `http:` or `https:` and nothing else. */
function isPlainWebUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    /* Not a URL at all. */
    return false;
  }
}
